import React, { useContext } from 'react';
import cx from 'classnames';
import { FlexLayout } from '../flex/FlexLayout.component';
import { FlexItem } from '../flex/FlexItem.component';
import { FlexLayoutModifier } from '../flex/flex.constants';
import { ELSIcon } from '../els.components';
import { IconPrefixes } from '../../constants/hmds.constants';
import { SherpathAIChatToggle } from '../sherpath-ai-chat-toggle/SherpathAIChatToggle.component';
import { getIsAIChatSidebarEnabled } from '../app/app-state-selectors.utilities';
import { AppContext } from '../app/AppProvider.component';
import { PageContextType } from '../../utilities/sherpath-ai-context.utilities';
import { getProduct, getProductConfig } from '../../utilities/product.utilities';

export type RemediationHeaderProps = {
  handleReturnNavigation: () => void;
  showSherpathAIChatToggle: boolean;
};

const RemediationHeader = (props: RemediationHeaderProps): JSX.Element => {

  const {
    appState,
  } = useContext(AppContext);

  const {
    handleReturnNavigation,
    showSherpathAIChatToggle
  } = props;

  const baseState = appState.remediationBaseState;
  const isAIChatSidebarEnabled = showSherpathAIChatToggle && getIsAIChatSidebarEnabled(appState, baseState?.remediationAssignmentWithSources);
  const product = getProduct(baseState?.remediationAssignmentWithSources);
  const { header } = getProductConfig(product);

  // Don't flash default (HESI) branding while the assignment is still loading. When there is no
  // assignment because the assessment is still pending, the default header (with its back link) is kept.
  if (!product && !baseState?.showPendingAssessmentMessage) {
    return null;
  }

  const backButton = (
    <button
        onClick={handleReturnNavigation}
        type="button"
        id="personalized-learning-plan-back-btn"
        className="u-els-anchorize u-els-display-block"
    >
      <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
        <FlexItem>
          <ELSIcon
              prefix={IconPrefixes.GIZMO}
              name="arrow-left"
              size="1x"
          />
        </FlexItem>
        <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
          {header.backLinkText}
        </FlexItem>
      </FlexLayout>
    </button>
  );

  return (
    <div className="c-nra-remediation-home__header">
      <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS]}>
        <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
          {header.logo ? (
            <>
              <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS, FlexLayoutModifier.LEFT]}>
                <FlexItem>
                  <ELSIcon
                    prefix={header.logo.prefix}
                    name={header.logo.name}
                    size={header.logo.size}
                  />
                </FlexItem>
                <FlexItem>
                  <h1 className={cx('u-els-font-size-h3', header.titleClass)}>{header.title}</h1>
                </FlexItem>
              </FlexLayout>
              {backButton}
            </>
          ) : (
            <>
              {backButton}
              <h1 className={cx('u-els-font-size-h3', header.titleClass)}>{header.title}</h1>
            </>
          )}
        </FlexItem>
        <FlexItem>
          <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS_2X]}>
            <FlexItem>
              <button
                  type="button"
                  id={header.tourButtonId}
                  className="u-els-anchorize u-els-display-block"
              >
                <FlexLayout modifiers={[FlexLayoutModifier.MIDDLE, FlexLayoutModifier.GUTTERS_1o2]}>
                  <FlexItem classes={['u-els-margin-top-1o4']}>
                    <ELSIcon
                        prefix={IconPrefixes.GIZMO}
                        name="directions"
                        size="1x"
                    />
                  </FlexItem>
                  <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                    Take a tour of this page
                  </FlexItem>
                </FlexLayout>
              </button>
            </FlexItem>
            <FlexItem isRender={isAIChatSidebarEnabled}>
              <SherpathAIChatToggle
                  remediationAssignmentWithSources={baseState?.remediationAssignmentWithSources}
                  pageContextType={PageContextType.HOME}
              />
            </FlexItem>
          </FlexLayout>
        </FlexItem>
      </FlexLayout>
    </div>
  );
};
export default RemediationHeader;
